import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { useTypography } from '../../hooks/useTypography';
import styles from './styles.module.css';
import { useTheme } from '../ThemeProvider';
const LINE_STUB = 'X';
function useTextareaAutoResize({ value, className, style, }) {
    const mockRef = useRef(null);
    const prevHeight = useRef(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [textareaHeight, setTextareaHeight] = useState(0);
    const updateHeight = () => {
        var _a;
        const mock = mockRef.current;
        if (!mock)
            return;
        if (prevHeight.current && Math.abs(prevHeight.current - mock.offsetHeight) <= 1)
            return;
        prevHeight.current = mock.offsetHeight;
        setTextareaHeight((_a = mock.scrollHeight) !== null && _a !== void 0 ? _a : mock.offsetHeight);
        setContainerHeight(mock.offsetHeight);
    };
    useLayoutEffect(() => {
        const mock = mockRef.current;
        if (!mock)
            return;
        updateHeight();
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(updateHeight);
            observer.observe(mock);
            return () => observer.unobserve(mock);
        }
    }, [className, style, value]);
    let mockText = `${value || LINE_STUB}`;
    // высота с просто пустой строкой на конце будет другая, и будет прыгать при вводе
    // потому корректируем
    if (mockText.slice(-1) === '\n') {
        mockText += LINE_STUB;
    }
    return { containerHeight, textareaHeight, mockText, mockRef };
}
export const TextInput = forwardRef(function TextInput(props, ref) {
    const { variant, className, thickness, group = 'tight', children, color, value, onChange, defaultValue, style, multiline, ...rest } = props;
    const theme = useTheme();
    const { className: typographyClasses, DefaultComponent } = useTypography({
        variant,
        thickness,
        group,
        color,
    });
    const containerClassName = cn(styles.textInput, typographyClasses, className);
    const { containerHeight, textareaHeight, mockText, mockRef } = useTextareaAutoResize({
        value: value || defaultValue,
        className: containerClassName,
        style,
    });
    const finalStyle = useMemo(() => {
        const height = multiline ? containerHeight : theme.fonts.heights[variant][group];
        const variables = {
            ['--input-height']: height ? `${height}px` : undefined,
        };
        return { ...variables, ...style };
    }, [theme, style, multiline, containerHeight]);
    const inputCommonProps = {
        className: cn(multiline ? styles.textarea : styles.input),
        value: onChange ? value : undefined,
        defaultValue: onChange ? defaultValue : value || defaultValue,
        onChange,
        ...rest,
    };
    return (React.createElement("div", { style: finalStyle, className: containerClassName }, multiline ? (React.createElement(React.Fragment, null,
        React.createElement("textarea", { style: { height: textareaHeight }, ...inputCommonProps, ref: ref }),
        React.createElement(DefaultComponent, { ref: mockRef, className: cn(styles.mock, inputCommonProps.className, {
                [styles.hidden]: !!textareaHeight,
            }) }, mockText))) : (React.createElement("input", { type: "text", ...inputCommonProps, ref: ref }))));
});
